/**
 * 音声録音用カスタムフック
 * WebAudio APIを使用してブラウザ内で音声を録音
 * 5分ごとにチャンク分割してアップロード可能
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const CHUNK_INTERVAL = 5 * 60 * 1000; // 5分ごとにチャンク分割

export const useAudioRecorder = (options = {}) => {
  const {
    onChunkReady,
    onError,
    mimeType = 'audio/webm;codecs=opus',
  } = options;

  // 状態
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const durationIntervalRef = useRef(null);
  const chunkIntervalRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);

  // 音声レベルの監視
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // 平均音量を計算
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(Math.min(100, (average / 128) * 100));

    if (isRecording && !isPaused) {
      animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
    }
  }, [isRecording, isPaused]);

  // チャンクを作成して送信
  const createChunk = useCallback(() => {
    if (chunksRef.current.length === 0) return null;

    const blob = new Blob(chunksRef.current, { type: mimeType });
    const chunkData = {
      blob,
      index: chunkIndexRef.current,
      timestamp: Date.now(),
    };

    // チャンクをリセット
    chunksRef.current = [];
    chunkIndexRef.current += 1;

    if (onChunkReady) {
      onChunkReady(chunkData);
    }

    return chunkData;
  }, [mimeType, onChunkReady]);

  // 録音開始
  const startRecording = useCallback(async () => {
    try {
      setError(null);

      // マイクへのアクセス許可を取得
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      // AudioContextを作成して音声レベル監視
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // MediaRecorderを作成
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'audio/webm',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      chunkIndexRef.current = 0;

      // データが利用可能になったとき
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // 録音停止時
      mediaRecorder.onstop = () => {
        // 残りのチャンクを処理
        createChunk();
      };

      // エラー時
      mediaRecorder.onerror = (event) => {
        const errorMsg = '録音中にエラーが発生しました';
        setError(errorMsg);
        if (onError) onError(errorMsg);
      };

      // 録音開始
      mediaRecorder.start(1000); // 1秒ごとにデータを取得
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);

      // 録音時間のカウント
      durationIntervalRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);

      // チャンク分割タイマー
      chunkIntervalRef.current = setInterval(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
          createChunk();
          mediaRecorderRef.current.start(1000);
        }
      }, CHUNK_INTERVAL);

      // 音声レベル監視開始
      monitorAudioLevel();

    } catch (err) {
      console.error('Recording start error:', err);
      let errorMsg = '録音の開始に失敗しました';

      if (err.name === 'NotAllowedError') {
        errorMsg = 'マイクへのアクセスが許可されていません。ブラウザの設定を確認してください。';
      } else if (err.name === 'NotFoundError') {
        errorMsg = 'マイクが見つかりません。マイクが接続されているか確認してください。';
      }

      setError(errorMsg);
      if (onError) onError(errorMsg);
    }
  }, [mimeType, createChunk, monitorAudioLevel, onError]);

  // 録音停止
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.onstop = () => {
          const finalChunk = createChunk();

          // クリーンアップ
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }

          if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }

          clearInterval(durationIntervalRef.current);
          clearInterval(chunkIntervalRef.current);
          cancelAnimationFrame(animationFrameRef.current);

          setIsRecording(false);
          setIsPaused(false);
          setAudioLevel(0);

          // 全チャンクを結合したBlobを返す
          resolve(finalChunk);
        };

        mediaRecorderRef.current.stop();
      } else {
        resolve(null);
      }
    });
  }, [createChunk]);

  // 一時停止
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(durationIntervalRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  // 再開
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      durationIntervalRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);

      monitorAudioLevel();
    }
  }, [monitorAudioLevel]);

  // 録音データを取得（全チャンク結合）
  const getRecordingBlob = useCallback(() => {
    if (chunksRef.current.length === 0) return null;
    return new Blob(chunksRef.current, { type: mimeType });
  }, [mimeType]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      clearInterval(durationIntervalRef.current);
      clearInterval(chunkIntervalRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  // 時間フォーマット
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    // 状態
    isRecording,
    isPaused,
    duration,
    formattedDuration: formatDuration(duration),
    audioLevel,
    error,

    // アクション
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    getRecordingBlob,

    // ユーティリティ
    formatDuration,
  };
};

export default useAudioRecorder;
